export type GuidePhase = 'install' | 'connect' | 'receive' | 'send';

export interface GuideStepContent {
  title: string;
  phase: GuidePhase;
  body: string[];
  tips?: string[];
  warnings?: string[];
  image?: {
    src: string;
    alt: string;
    caption: string;
  };
  link?: {
    href: string;
    label: string;
  };
}

export const GUIDE_PHASES: { id: GuidePhase; label: string }[] = [
  { id: 'install', label: 'Install' },
  { id: 'connect', label: 'Connect' },
  { id: 'receive', label: 'Receive' },
  { id: 'send', label: 'Send' },
];

export const PHANTOM_CONTENT = {
  title: 'Phantom wallet guide',
  lead:
    'Phantom is a popular Solana browser wallet. Follow these steps to install it, connect to Canadawale Yar, and send or receive CWY.',
  steps: [
    {
      title: 'Install Phantom',
      phase: 'install',
      body: [
        'Open the official Phantom download page and choose your browser: Chrome, Brave, or Edge.',
        'Always use phantom.com — do not install Phantom from random ads or email links.',
      ],
      link: {
        href: 'https://phantom.com/download',
        label: 'Open phantom.com/download',
      },
      image: {
        src: '/wallet_ui/phantom-download.jpg',
        alt: 'Phantom for desktop download page with Chrome, Brave, and Edge options',
        caption: 'On phantom.com/download, pick Chrome, Brave, or Edge to install the extension.',
      },
    },
    {
      title: 'Add the browser extension',
      phase: 'install',
      body: [
        'After you choose a browser, you will land on the Phantom page in the Chrome Web Store (Brave and Edge use the same store).',
        'Click Add to Chrome (or Add to Brave / Add to Edge), then confirm Add extension.',
      ],
      image: {
        src: '/wallet_ui/phantom-chrome-store.jpg',
        alt: 'Chrome Web Store page for the Phantom extension with Add to Chrome button',
        caption: 'Click Add to Chrome, then confirm to install the Phantom extension.',
      },
    },
    {
      title: 'Create or import your wallet',
      phase: 'install',
      body: [
        'Open the Phantom extension from your browser toolbar (puzzle icon if you do not see the ghost logo yet).',
        'Create a new wallet or import an existing one. Set a strong password for this device.',
      ],
      warnings: [
        'Write down your recovery phrase on paper and store it offline. Anyone with that phrase can take your tokens.',
        'Never paste your recovery phrase into a website or chat.',
      ],
    },
    {
      title: 'Open the CWY Wallet page',
      phase: 'connect',
      body: [
        'Sign in to Canadawale Yar and go to Profile → Wallet.',
        'You should see No wallet connected and a red Connect wallet button in the top right.',
      ],
      image: {
        src: '/wallet_ui/cwy-connect-wallet.jpg',
        alt: 'Canadawale Yar wallet page showing No wallet connected and Connect wallet button',
        caption: 'On the CWY Wallet page, click Connect wallet when you are ready to link Phantom.',
      },
    },
    {
      title: 'Connect to CWY',
      phase: 'connect',
      body: [
        'Click Connect wallet, then choose Phantom from the list.',
        'A Phantom popup will ask you to connect. Review the request and click Connect / Approve.',
        'You may be asked to sign a short message to prove you own the wallet. This does not send money — it only confirms ownership.',
      ],
      tips: [
        'If Phantom does not appear in the list, make sure the extension is installed and unlocked, then refresh the page.',
      ],
    },
    {
      title: 'Receive CWY',
      phase: 'receive',
      body: [
        'Once your wallet is connected, use Receive on the Wallet page (Request CWY tokens).',
        'Enter the amount you want to request and submit. Depending on the program rules, a request may need approval before CWY is sent.',
        'When the transfer completes, CWY appears in your Phantom wallet under your Solana tokens. You can also see status under transaction history in the app.',
      ],
    },
    {
      title: 'Send CWY',
      phase: 'send',
      body: [
        'To return CWY to the CWY vault from the app, use Send on the Wallet page (Return CWY tokens). Confirm the amount and approve the transaction in Phantom when prompted.',
        "To send CWY to another person's Solana address, open the Phantom extension, tap Send, choose CWY, enter their address carefully, and confirm.",
      ],
      warnings: [
        'Always double-check the destination address. Crypto transfers cannot be undone.',
      ],
      tips: [
        'You need a little SOL in Phantom to pay Solana network fees when sending.',
      ],
    },
  ] satisfies GuideStepContent[],
} as const;
